import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { 
    GET_PRODUCT, 
    GET_MAIN_CATEGORIES, 
    CREATE_PRODUCT, 
    UPDATE_PRODUCT 
} from '../../apollo/queries';
import { uploadFileToS3, validateFile } from '../../utils/s3Upload';
import '../css/ProductFormPage.css';

const ProductFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // 파일 입력 참조들
    const image1Ref = useRef(null);
    const image2Ref = useRef(null);
    const image3Ref = useRef(null);
    const image4Ref = useRef(null);
    const cadFileRef = useRef(null);
    const pdfFileRef = useRef(null);

    const isEditMode = location.pathname.includes('/edit');
    const productId = searchParams.get('id');

    const pageTitle = isEditMode ? '상품 정보 수정' : '신규 상품 등록';
    const breadcrumbPath = isEditMode ? '상품 정보 수정' : '신규 상품 등록';

    // 폼 상태
    const [formData, setFormData] = useState({
        productCode: '',
        name: '',
        size: '',
        mainCategoryId: '',
        subCategoryId: '',
        options: [], // 옵션 배열
        description: '', // 상세 설명
        images: [], // 상품 이미지 URLs
        cadFiles: [], // CAD 파일 URLs
        pdfFiles: [] // PDF 파일 URLs
    });

    // 파일 업로드 상태
    const [fileStates, setFileStates] = useState({
        image1: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
        image2: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
        image3: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
        image4: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
        cadFile: { files: [], uploading: false, fileNames: [] },
        pdfFile: { files: [], uploading: false, fileNames: [] }
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // GraphQL 쿼리
    const { data: categoriesData } = useQuery(GET_MAIN_CATEGORIES);
    const { data: productData, loading: productLoading, refetch } = useQuery(GET_PRODUCT, {
        variables: { id: productId },
        skip: !isEditMode || !productId
    });

    // GraphQL 뮤테이션
    const [createProduct] = useMutation(CREATE_PRODUCT, {
        onCompleted: (data) => {
            console.log('=== 제품 생성 완료 ===');
            console.log('생성된 제품:', data.createProduct);
            alert('제품이 성공적으로 등록되었습니다.');
            navigate('/admin/products');
        },
        onError: (error) => {
            console.error('제품 생성 에러:', error);
            alert('제품 등록 중 오류가 발생했습니다.');
            setIsSubmitting(false);
        }
    });

    const [updateProduct] = useMutation(UPDATE_PRODUCT, {
        onCompleted: (data) => {
            console.log('=== 제품 업데이트 완료 ===');
            console.log('업데이트된 제품:', data.updateProduct);
            alert('제품이 성공적으로 수정되었습니다.');
            
            // 업데이트 후 제품 데이터 다시 가져오기
            if (isEditMode && productId) {
                console.log('=== 제품 데이터 다시 로드 ===');
                refetch({
                    id: productId
                }).then((result) => {
                    console.log('Refetch 완료:', result);
                }).catch((error) => {
                    console.error('Refetch 에러:', error);
                });
            }
            
            navigate('/admin/products');
        },
        onError: (error) => {
            console.error('제품 업데이트 에러:', error);
            alert('제품 수정 중 오류가 발생했습니다.');
            setIsSubmitting(false);
        }
    });

    // 편집 모드에서 제품 데이터 로드 시 이미지 처리
    useEffect(() => {
        if (isEditMode && productData?.product) {
            const product = productData.product;
            
            // 안전한 JSON 파싱 - 개선된 버전
            let existingImages = [];
            if (product.images) {
                try {
                    if (typeof product.images === 'string') {
                        const cleanedImages = product.images.trim();
                        
                        if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
                            // JSON 문자열이 완전한지 확인
                            if (cleanedImages.startsWith('[') && cleanedImages.endsWith(']')) {
                                try {
                                    existingImages = JSON.parse(cleanedImages);
                                } catch (parseError) {
                                    // JSON 파싱 실패 시 URL 패턴 매칭으로 추출
                                    const urlPattern = /https?:\/\/[^\s,"\]]+/g;
                                    const matches = cleanedImages.match(urlPattern);
                                    if (matches) {
                                        existingImages = matches;
                                    }
                                }
                            } else if (cleanedImages.startsWith('[') && !cleanedImages.endsWith(']')) {
                                // JSON이 잘린 경우 URL 패턴으로 추출
                                const urlPattern = /https?:\/\/[^\s,"\]]+/g;
                                const matches = cleanedImages.match(urlPattern);
                                if (matches) {
                                    existingImages = matches;
                                }
                            } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                                // 단일 URL 문자열인 경우
                                existingImages = [cleanedImages];
                            }
                        }
                    } else if (Array.isArray(product.images)) {
                        existingImages = product.images;
                    }
                    
                    // 배열이 아닌 경우 빈 배열로 처리
                    if (!Array.isArray(existingImages)) {
                        existingImages = [];
                    }
                    
                    // 빈 문자열이나 null 요소 제거 및 URL 유효성 검사
                    existingImages = existingImages.filter(url => {
                        if (!url || typeof url !== 'string') return false;
                        const trimmedUrl = url.trim();
                        return trimmedUrl !== '' && (trimmedUrl.includes('http') || trimmedUrl.includes('/'));
                    });
                    
                } catch (error) {
                    existingImages = [];
                }
            }
            
            setFormData({
                productCode: product.productCode || '',
                name: product.name || '',
                size: product.size || '',
                mainCategoryId: product.mainCategory?.id || '',
                subCategoryId: product.subCategory?.id || '',
                options: product.options || [],
                description: product.description || '',
                images: existingImages, // 파싱된 이미지 배열 사용
                cadFiles: product.cadFiles || [],
                pdfFiles: product.pdfFiles || []
            });

            // 기존 이미지들을 파일 상태에 반영 - 개선된 버전
            setFileStates(prev => {
                const newFileStates = {
                    image1: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                    image2: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                    image3: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                    image4: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                    cadFile: { files: [], uploading: false, fileNames: [] },
                    pdfFile: { files: [], uploading: false, fileNames: [] }
                };
                
                // 기존 이미지들을 파일 상태에 설정
                existingImages.forEach((url, index) => {
                    if (index < 4 && url && typeof url === 'string' && url.trim() !== '') {
                        const imageKey = `image${index + 1}`;

                        newFileStates[imageKey] = {
                            file: null,
                            url: url.trim(),
                            preview: url.trim(),
                            uploading: false,
                            fileName: `기존 이미지 ${index + 1}`
                        };
                    }
                });

                // 기존 CAD 파일들 반영
                if (product.cadFiles && product.cadFiles.length > 0) {
                    newFileStates.cadFile = {
                        files: product.cadFiles,
                        uploading: false,
                        fileNames: product.cadFiles.map(file => file.name || 'CAD 파일')
                    };
                }

                // 기존 PDF 파일들 반영  
                if (product.pdfFiles && product.pdfFiles.length > 0) {
                    newFileStates.pdfFile = {
                        files: product.pdfFiles,
                        uploading: false,
                        fileNames: product.pdfFiles.map(file => file.name || 'PDF 파일')
                    };
                }
                
                return newFileStates;
            });
        } else if (!isEditMode) {
            // 신규 생성 모드일 때 파일 상태 초기화
            setFileStates({
                image1: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                image2: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                image3: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                image4: { file: null, url: '', preview: '', uploading: false, fileName: '선택된 파일 없음' },
                cadFile: { files: [], uploading: false, fileNames: [] },
                pdfFile: { files: [], uploading: false, fileNames: [] }
            });
        }
    }, [isEditMode, productData]);

    const categories = categoriesData?.mainCategories || [];
    const selectedCategory = categories.find(cat => cat.id === formData.mainCategoryId);
    const subCategories = selectedCategory?.subCategories || [];

    // 폼 입력 핸들러
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 옵션 추가
    const addOption = () => {
        setFormData(prev => ({
            ...prev,
            options: [...prev.options, '']
        }));
    };

    // 옵션 수정
    const updateOption = (index, value) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.map((option, i) => i === index ? value : option)
        }));
    };

    // 옵션 삭제
    const removeOption = (index) => {
        setFormData(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    // 이미지 파일 선택 핸들러
    const handleImageFileSelect = (imageKey) => {
        const refs = { image1: image1Ref, image2: image2Ref, image3: image3Ref, image4: image4Ref };
        refs[imageKey].current?.click();
    };

    // 이미지 파일 업로드 핸들러
    const handleImageFileChange = async (e, imageKey) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            // 파일 유효성 검사
            validateFile(file, 50 * 1024 * 1024, ['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

            // 업로드 시작
            setFileStates(prev => {
                const newState = {
                    ...prev,
                    [imageKey]: {
                        ...prev[imageKey],
                        file: file,
                        fileName: file.name,
                        uploading: true,
                        url: '',
                        preview: ''
                    }
                };
                return newState;
            });

            // 로컬 미리보기 생성
            const reader = new FileReader();
            reader.onload = (e) => {
                setFileStates(prev => {
                    const newState = {
                        ...prev,
                        [imageKey]: {
                            ...prev[imageKey],
                            preview: e.target.result
                        }
                    };
                    return newState;
                });
            };
            reader.readAsDataURL(file);

            // S3 업로드
            const s3Url = await uploadFileToS3(file);
            
            if (s3Url) {
                
                // 파일 상태 업데이트
                setFileStates(prev => {
                    const newState = {
                        ...prev,
                        [imageKey]: {
                            ...prev[imageKey],
                            url: s3Url,
                            preview: s3Url,
                            uploading: false
                        }
                    };
                    return newState;
                });
                
                // formData.images도 즉시 업데이트 - 이것이 핵심 수정 사항
                const imageIndex = parseInt(imageKey.replace('image', '')) - 1;
                
                setFormData(prev => {
                    const newImages = [...(prev.images || [])];
                    
                    // 배열 크기를 필요에 따라 확장
                    while (newImages.length <= imageIndex) {
                        newImages.push('');
                    }
                    newImages[imageIndex] = s3Url;
                    
                    const newFormData = {
                        ...prev,
                        images: newImages
                    };
                    return newFormData;
                });
                
            } else {
                throw new Error('S3 업로드 실패');
            }

        } catch (error) {
            alert(`이미지 업로드 실패: ${error.message}`);
            
            // 실패 시 초기화
            setFileStates(prev => ({
                ...prev,
                [imageKey]: {
                    ...prev[imageKey],
                    file: null,
                    fileName: '선택된 파일 없음',
                    uploading: false,
                    url: '',
                    preview: ''
                }
            }));
        }
    };

    // 이미지 제거 핸들러
    const handleRemoveImage = (imageKey) => {
        // 이미지 인덱스 계산 (image1 → 0, image2 → 1, ...)
        const imageIndex = parseInt(imageKey.replace('image', '')) - 1;
        
        // formData.images 배열에서도 해당 인덱스의 이미지 제거
        setFormData(prev => {
            const newImages = [...(prev.images || [])];
            if (imageIndex >= 0 && imageIndex < newImages.length) {
                newImages[imageIndex] = null; // 인덱스 유지를 위해 null로 설정
            }
            return {
                ...prev,
                images: newImages.filter(img => img !== null) // null 제거
            };
        });
        
        setFileStates(prev => ({
            ...prev,
            [imageKey]: {
                file: null,
                url: '',
                preview: '',
                uploading: false,
                fileName: '선택된 파일 없음'
            }
        }));
    };

        // CAD/PDF 파일 업로드 핸들러
    const handleMultiFileUpload = async (e, fileType) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        try {
            setFileStates(prev => ({
                ...prev,
                [fileType]: {
                    ...prev[fileType],
                    uploading: true
                }
            }));

            const uploadPromises = files.map(async (file) => {
                try {
                    // 파일 크기만 체크 (타입은 유연하게)
                    if (file.size > 50 * 1024 * 1024) { // 50MB 제한
                        throw new Error(`${file.name}: 파일 크기가 너무 큽니다. (최대 50MB)`);
                    }
                    
                    const url = await uploadFileToS3(file);
                    return { 
                        name: file.name, 
                        url,
                        size: file.size,
                        type: file.type
                    };
                } catch (error) {
                    console.error(`${file.name} 업로드 실패:`, error);
                    return null;
                }
            });

            const results = await Promise.all(uploadPromises);
            const successfulUploads = results.filter(result => result !== null);

            setFileStates(prev => ({
                ...prev,
                [fileType]: {
                    files: [...prev[fileType].files, ...successfulUploads],
                    uploading: false,
                    fileNames: [...prev[fileType].fileNames, ...successfulUploads.map(item => item.name)]
                }
            }));

            if (successfulUploads.length > 0) {
                console.log(`${successfulUploads.length}개 파일이 성공적으로 업로드되었습니다.`);
            }

        } catch (error) {
            console.error('파일 업로드 에러:', error);
            alert(`파일 업로드 실패: ${error.message}`);
            
            setFileStates(prev => ({
                ...prev,
                [fileType]: {
                    ...prev[fileType],
                    uploading: false
                }
            }));
        }
    };

    // 파일 제거 핸들러 (CAD/PDF용)
    const handleRemoveFile = (fileType, index) => {
        setFileStates(prev => ({
            ...prev,
            [fileType]: {
                ...prev[fileType],
                files: prev[fileType].files.filter((_, i) => i !== index),
                fileNames: prev[fileType].fileNames.filter((_, i) => i !== index)
            }
        }));
    };

    // 파일 크기 포맷팅 함수
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // 파일 미리보기/다운로드 핸들러
    const handleFileAction = (file) => {
        if (file.type === 'application/pdf') {
            // PDF는 새 탭에서 미리보기
            window.open(file.url, '_blank');
        } else {
            // CAD 파일은 다운로드
            const link = document.createElement('a');
            link.href = file.url;
            link.download = file.name;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // 이미지 클릭 핸들러 (새 탭에서 열기)
    const handleImageClick = (imageUrl) => {
        if (imageUrl) {
            window.open(imageUrl, '_blank');
        }
    };

    // 폼 제출 핸들러
    const handleSubmit = async () => {
        
        if (!formData.productCode.trim()) {
            alert('상품 코드를 입력해주세요.');
            return;
        }

        if (!formData.name.trim()) {
            alert('상품명을 입력해주세요.');
            return;
        }

        if (!formData.mainCategoryId) {
            alert('메인 카테고리를 선택해주세요.');
            return;
        }

        try {
            setIsSubmitting(true);

            // 빈 옵션 제거
            const cleanOptions = formData.options.filter(option => option.trim() !== '');

            // 이미지 URLs 수집 (기존 이미지 + 새로 업로드된 이미지)
            const imageUrls = [];
            
            // 먼저 formData.images에서 기존 이미지들을 가져옴
            if (formData.images && Array.isArray(formData.images)) {
                formData.images.forEach((url, index) => {
                    if (url && typeof url === 'string' && url.trim() !== '') {
                        imageUrls[index] = url.trim();
                    }
                });
            }
            
            // 그 다음 fileStates에서 새로 업로드된 이미지들을 확인하여 업데이트
            ['image1', 'image2', 'image3', 'image4'].forEach((imageKey, index) => {
                const fileState = fileStates[imageKey];
                
                if (fileState?.url && typeof fileState.url === 'string' && fileState.url.trim() !== '') {
                    // 해당 인덱스에 이미지가 있으면 교체, 없으면 추가
                    imageUrls[index] = fileState.url.trim();
                }
            });
            
            // 빈 슬롯 제거 (undefined나 빈 문자열)
            const finalImageUrls = imageUrls.filter(url => url && typeof url === 'string' && url.trim() !== '');

            // 안전한 JSON 문자열 생성
            let imagesJson = null;
            if (finalImageUrls.length > 0) {
                try {
                    imagesJson = JSON.stringify(finalImageUrls);
                } catch (error) {
                    console.error('이미지 JSON 생성 에러:', error);
                    imagesJson = null;
                }
            }

            // CAD/PDF 파일 데이터 준비
            let cadFilesJson = null;
            let pdfFilesJson = null;
            
            if (fileStates.cadFile.files.length > 0) {
                try {
                    cadFilesJson = JSON.stringify(fileStates.cadFile.files);
                } catch (error) {
                    console.error('CAD 파일 JSON 생성 에러:', error);
                }
            }
            
            if (fileStates.pdfFile.files.length > 0) {
                try {
                    pdfFilesJson = JSON.stringify(fileStates.pdfFile.files);
                } catch (error) {
                    console.error('PDF 파일 JSON 생성 에러:', error);
                }
            }

            const input = {
                productCode: formData.productCode.trim(),
                name: formData.name.trim(),
                size: formData.size.trim() || null,
                mainCategoryId: formData.mainCategoryId,
                subCategoryId: formData.subCategoryId || null,
                options: cleanOptions.length > 0 ? cleanOptions : [],
                description: formData.description.trim() || null,
                // 기존 필드들은 기본값으로 설정
                specifications: null,
                price: 0,
                discountPrice: null,
                stock: 0,
                images: imagesJson,
                cadFiles: cadFilesJson,
                pdfFiles: pdfFilesJson,
                isActive: true
            };

            if (isEditMode) {
                const result = await updateProduct({
                    variables: { id: productId, input }
                });
            } else {
                const result = await createProduct({
                    variables: { input }
                });
            }
        } catch (error) {
            console.error('제품 저장 에러:', error);
            alert(`제품 저장 중 오류가 발생했습니다: ${error.message}`);
            setIsSubmitting(false);
        }
    };

    // 로딩 상태
    if (productLoading) {
        return (
            <>
                <div className="breadcrumb">
                    <span>홈 &gt; 상품 관리 &gt; {breadcrumbPath}</span>
                </div>
                <div className="admin-main-content">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <div>제품 정보를 불러오는 중입니다...</div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 상품 관리 &gt; {breadcrumbPath}</span>
            </div>
            <div className="admin-main-content">
                <main className="admin-content-form">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>{pageTitle}</h2>
                    </div>

                    {/* 기본 정보 섹션 */}
                    <div className="form-section">
                        <div className="section-header">
                            <h3 className="section-title">기본 정보</h3>
                            <hr className="section-divider" />
                    </div>

                        <table className="info-table">
                            <tbody>
                                <tr>
                                    <th>상품 코드<br/>(제품번호)</th>
                                    <td>
                                        <input 
                                            type="text" 
                                            name="productCode"
                                            value={formData.productCode}
                                            onChange={handleInputChange}
                                            placeholder="BW-M2-0520300-CT" 
                                            className="form-input"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>상품 명</th>
                                    <td>
                                        <input 
                                            type="text" 
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="메인 204 부스바 CT 타입" 
                                            className="form-input"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>사이즈</th>
                                    <td>
                                        <input 
                                            type="text" 
                                            name="size"
                                            value={formData.size}
                                            onChange={handleInputChange}
                                            placeholder="8T * 5T x 20 x 500" 
                                            className="form-input"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>옵션 정보</th>
                                    <td>
                                        <div className="options-container">
                                            {formData.options.map((option, index) => (
                                                <div key={index} className="option-row">
                                        <input 
                                                        type="text"
                                                        value={option}
                                                        onChange={(e) => updateOption(index, e.target.value)}
                                                        placeholder={index === 0 ? "니켈도금" : `옵션 ${index + 1}`}
                                                        className="option-input"
                                        />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeOption(index)}
                                                        className="btn-remove-option"
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            ))}
                                            
                                            {formData.options.length < 3 && (
                                                <div className="option-row">
                                        <input 
                                                        type="text"
                                                        placeholder={`옵션 ${formData.options.length + 1}`}
                                                        className="option-input"
                                                        disabled
                                                        style={{ backgroundColor: '#f5f5f5' }}
                                                    />
                                                </div>
                                            )}
                                            
                                            <button 
                                                type="button"
                                                onClick={addOption}
                                                className="btn-add-option"
                                            >
                                                + 옵션 추가
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>메인 카테고리 → 서브 카테고리 선택</th>
                                    <td>
                                        <div className="category-selectors">
                                            <select 
                                                value={formData.mainCategoryId} 
                                                onChange={(e) => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        mainCategoryId: e.target.value,
                                                        subCategoryId: ''
                                                    }));
                                                }}
                                                className="category-select"
                                            >
                                                <option value="">메인 카테고리 선택</option>
                                                {categories.map(category => (
                                                    <option key={category.id} value={category.id}>{category.name}</option>
                                                ))}
                                            </select>
                                            <span className="arrow">→</span>
                                            <select 
                                                value={formData.subCategoryId} 
                                                onChange={(e) => setFormData(prev => ({ ...prev, subCategoryId: e.target.value }))}
                                                disabled={!formData.mainCategoryId}
                                                className="category-select"
                                            >
                                                <option value="">서브 카테고리 선택</option>
                                                {subCategories.map(subCategory => (
                                                    <option key={subCategory.id} value={subCategory.id}>{subCategory.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* 상품 이미지 및 상세정보 섹션 */}
                    <div className="form-section">
                        <div className="section-header">
                        <h3 className="section-title">상품 이미지 및 상세정보</h3>
                            <hr className="section-divider" />
                        </div>
                        
                        <table className="info-table">
                            <tbody>
                                {['image1', 'image2', 'image3', 'image4'].map((imageKey, index) => (
                                    <tr key={imageKey}>
                                        <th>이미지 {index + 1} {index < 2 ? '*' : ''}</th>
                                        <td>
                                            <div className="file-upload-section">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                    onChange={(e) => handleImageFileChange(e, imageKey)}
                                                    className="file-input"
                                                    ref={imageKey === 'image1' ? image1Ref : imageKey === 'image2' ? image2Ref : imageKey === 'image3' ? image3Ref : image4Ref}
                                            />
                                            <button 
                                                    type="button"
                                                    onClick={() => handleImageFileSelect(imageKey)}
                                                    className="file-upload-btn"
                                                    disabled={fileStates[imageKey].uploading}
                                            >
                                                    {fileStates[imageKey].uploading ? '업로드 중...' : '파일 선택'}
                                            </button>
                                                <span className="file-info">{fileStates[imageKey].fileName}</span>
                                            
                                                {fileStates[imageKey].url && (
                                                    <div className="upload-success">
                                                        <span className="success-text">✓ 업로드 완료</span>
                                                        <a 
                                                            href={fileStates[imageKey].url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="s3-link"
                                                        >
                                                            🔗 S3 링크 보기
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveImage(imageKey)}
                                                            className="btn-remove-file"
                                                        >
                                                            ✕ 제거
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {fileStates[imageKey].preview && (
                                                    <div className="image-preview-container">
                                                        <div className="image-preview-wrapper">
                                                            <img 
                                                                src={fileStates[imageKey].preview} 
                                                                alt={`이미지 ${index + 1} 미리보기`}
                                                                className="image-preview"
                                                                onClick={() => handleImageClick(fileStates[imageKey].url || fileStates[imageKey].preview)}
                                                            />
                                                            {fileStates[imageKey].uploading && (
                                                                <div className="upload-overlay">
                                                                    <div className="spinner"></div>
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                
                                <tr>
                                    <th>상세 설명</th>
                                    <td>
                                        <div className="editor-container">
                                            <div className="editor-toolbar">
                                                <button type="button" className="editor-btn">굵게</button>
                                                <button type="button" className="editor-btn">기울임</button>
                                                <button type="button" className="editor-btn">밑줄</button>
                                                <button type="button" className="editor-btn">글머리 기호</button>
                                                <button type="button" className="editor-btn">번호 매기기</button>
                                                <button type="button" className="editor-btn">링크</button>
                                                <button type="button" className="editor-btn">이미지</button>
                                            </div>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                                className="editor-textarea"
                                                rows="10"
                                                placeholder="상품에 대한 상세 설명을 입력하세요..."
                                            />
                                            <div className="editor-footer">
                                                <span>경고설명: 이온 이메일등의 민감한 정보 유출 고객님...</span>
                                                <div className="editor-actions">
                                                    <button type="button" className="btn-editor-action">Execute</button>
                                                    <button type="button" className="btn-editor-action">Help</button>
                                                    <button type="button" className="btn-editor-action">Test</button>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>CAD 파일</th>
                                    <td>
                                        <div className="file-upload-section">
                                            <div>
                                        <input
                                            type="file"
                                                    accept=".dwg,.dxf,.step,.stp,.iges,.igs"
                                                    onChange={(e) => handleMultiFileUpload(e, 'cadFile')}
                                                    multiple
                                                    className="file-input"
                                            ref={cadFileRef}
                                        />
                                        <button 
                                                    type="button"
                                            onClick={() => cadFileRef.current?.click()}
                                                    className="file-upload-btn"
                                                    disabled={fileStates.cadFile.uploading}
                                                >
                                                    {fileStates.cadFile.uploading ? '업로드 중...' : '파일 선택'}
                                                </button>
                                                <span className="file-info">
                                                    {fileStates.cadFile.fileNames.length > 0 
                                                        ? `${fileStates.cadFile.fileNames.length}개 파일 업로드됨` 
                                                        : '선택된 파일 없음'}
                                                </span>
                                            </div>
                                            
                                            {fileStates.cadFile.files.length > 0 && (
                                                <div className="file-list">
                                                    {fileStates.cadFile.files.map((file, index) => (
                                                        <div key={index} className="file-item">
                                                            <div className="file-details">
                                                                <span className="file-name">{file.name}</span>
                                                                <span className="file-size">({formatFileSize(file.size)})</span>
                                                            </div>
                                                            <div className="file-actions">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleFileAction(file)}
                                                                    className="btn-file-action download"
                                                                >
                                                                    📁 다운로드
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveFile('cadFile', index)}
                                                                    className="btn-remove-file"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>PDF 파일</th>
                                    <td>
                                        <div className="file-upload-section">
                                            <div>
                                        <input
                                            type="file"
                                                    accept=".pdf"
                                                    onChange={(e) => handleMultiFileUpload(e, 'pdfFile')}
                                                    multiple
                                                    className="file-input"
                                            ref={pdfFileRef}
                                        />
                                        <button 
                                                    type="button"
                                            onClick={() => pdfFileRef.current?.click()}
                                                    className="file-upload-btn"
                                                    disabled={fileStates.pdfFile.uploading}
                                                >
                                                    {fileStates.pdfFile.uploading ? '업로드 중...' : '파일 선택'}
                                                </button>
                                                <span className="file-info">
                                                    {fileStates.pdfFile.fileNames.length > 0 
                                                        ? `${fileStates.pdfFile.fileNames.length}개 파일 업로드됨` 
                                                        : '선택된 파일 없음'}
                                                </span>
                                            </div>
                                            
                                            {fileStates.pdfFile.files.length > 0 && (
                                                <div className="file-list">
                                                    {fileStates.pdfFile.files.map((file, index) => (
                                                        <div key={index} className="file-item">
                                                            <div className="file-details">
                                                                <span className="file-name">{file.name}</span>
                                                                <span className="file-size">({formatFileSize(file.size)})</span>
                                                            </div>
                                                            <div className="file-actions">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleFileAction(file)}
                                                                    className="btn-file-action preview"
                                                                >
                                                                    👁️ 미리보기
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveFile('pdfFile', index)}
                                                                    className="btn-remove-file"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="form-actions">
                        <button className="btn-back" onClick={() => navigate(-1)}>
                            <span className="arrow-icon">&lt;</span> 돌아가기
                        </button>
                        <button 
                            className="btn-save" 
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '처리 중...' : `${isEditMode ? '수정' : '저장'}`} <span className="arrow-icon">&gt;</span>
                        </button>
                    </div>
                </main>
            </div>
        </>
    );
};

export default ProductFormPage; 